import React, { useEffect, useState, createContext, useContext, ReactNode } from "react";
import { recordSentry } from "utils/recordSentry";
import { bulkDeleteFieldContinuationDatas, bulkSaveFieldContinuationDatas } from "../db/queries/fieldContinuationDataQueries";
import { bulkDeleteFieldTemplates, bulkSaveFieldTemplates } from "../db/queries/fieldTemplateQueries";
import { downloadFormTemplateJSON, putFormTemplate } from "../db/queries/formTemplateQueries";
import { bulkDeleteOptions, bulkSaveOptions } from "../db/queries/optionQueries";
import _createFormTemplate from "../utils/_createFormTemplate";
import _updateFormTemplate from "../utils/_updateFormTemplate";
import batchCreateFieldContinuationData from "../utils/batchCreateFieldContinuationData";
import batchCreateFieldTemplates from "../utils/batchCreateFieldTemplates";
import batchCreateOptions from "../utils/batchCreateOptions";
import { handleBatchCrud } from "../utils/handleBatchCrud";
import { handleDeletionOfExistingDocuments } from "utils/handleDeletionOfExistingDocuments";
import batchRemoveFieldTemplates from "utils/batchRemoveFieldTemplates";
import batchRemoveOptions from "utils/batchRemoveOptions";
import { FieldTemplate, FormTemplate, Option, FieldContinuationData, BatchDeleteInput, SyncConfig, FieldTemplateForResolver } from "db/definitions";
import { handleUploadToS3, verifyUpload } from "utils/handleUploadToS3";
import batchRemoveFieldContinuationData from "utils/batchRemoveFieldContinuationData";
import { handleDeleteFormTemplate } from "utils/handleDeleteFormTemplate";
import { mergeAndSaveTemplates, prepDbForTemplateMerge, prepTemplateJsonForMerge } from "db/merge";
import handleBuildFormTemplateBackupPath from "utils/s3/handleBuildFormTemplateBackupPath";

interface TemplateManagerContextType {
	createFormTemplate: (data: FormTemplate) => void;
	templates: Record<string, FormTemplate[]>;
	templateToEdit: FormTemplate | null;
	setTemplateToEdit: React.Dispatch<React.SetStateAction<FormTemplate | null>>;
	deleteFormTemplate: (id: string) => Promise<void>;
	updateFieldTemplates: (
		fieldTemplateData: Record<string, FieldTemplate>,
		optionsOutsideTableField: Option[],
		tableFieldTemplateData: Record<string, FieldTemplate>,
		formTemplateId: string,
		fieldsOrder: string[],
	) => Promise<void>;
	updateFormTemplate: (data: FormTemplate) => void;
	uploadFormTemplates: (files: File) => Promise<void>;
}

const TemplateManagerContext = createContext<TemplateManagerContextType | undefined>(undefined);

interface TemplateManagerProviderProps {
	children: ReactNode;
	initialData: {
		online: boolean;
		projectId: string;
		formTemplates: FormTemplate[];
	};
}

export const TemplateManagerProvider = ({ children, initialData }: TemplateManagerProviderProps) => {
	const { online, projectId, formTemplates: retrievedTemplates } = initialData;
	const [templates, setTemplates] = useState<Record<string, FormTemplate[]>>({});
	const [templateToEdit, setTemplateToEdit] = useState<FormTemplate | null>(null);

	useEffect(() => {
		const templateMap: Record<string, FormTemplate[]> = {};
		for (let i = 0; i < retrievedTemplates.length; i++) {
			const retrievedTemplate = retrievedTemplates[i];

			if (templateMap[retrievedTemplate.formTemplateGroup]) {
				templateMap[retrievedTemplate.formTemplateGroup].push(retrievedTemplate);
			} else {
				templateMap[retrievedTemplate.formTemplateGroup] = [retrievedTemplate];
			}
		}
		setTemplates(templateMap);
	}, [retrievedTemplates, projectId]);

	function updateFormTemplate(data: FormTemplate) {
		const copy = JSON.parse(JSON.stringify({ ...data }));
		const newTemplates = { ...templates };
		if (newTemplates[data.formTemplateGroup]) {
			let found = false;
			for (let i = 0; i < newTemplates[data.formTemplateGroup].length; i++) {
				const template = newTemplates[data.formTemplateGroup][i];
				if (template.id === data.id) {
					newTemplates[data.formTemplateGroup][i] = data;
					found = true;
				}
			}
			if (!found) {
				newTemplates[data.formTemplateGroup].push(data);
			}
		} else {
			newTemplates[data.formTemplateGroup] = [data];
		}

		delete copy.fieldTemplates;
		setTemplates(newTemplates);
		if (online) {
			_updateFormTemplate(copy);
		} else {
			putFormTemplate(copy);
		}
	}

	function createFormTemplate(data: FormTemplate) {
		let newTemplates = { ...templates };
		if (newTemplates[data.formTemplateGroup]) {
			let found = false;
			for (let i = 0; i < newTemplates[data.formTemplateGroup].length; i++) {
				const template = newTemplates[data.formTemplateGroup][i];
				if (template.id === data.id) {
					newTemplates[data.formTemplateGroup][i] = data;
					found = true;
				}
			}
			if (!found) {
				newTemplates[data.formTemplateGroup].push(data);
			}
		} else {
			newTemplates[data.formTemplateGroup] = [data];
		}

		setTemplates(newTemplates);
		delete data.fieldTemplates;
		if (online) {
			_createFormTemplate(data);
		} else {
			putFormTemplate(data);
		}
	}

	/**
	 * This handles making sure the data is ready for an update to the respective db (online is within hook), and then batch updates.
	 * This will also delete field templates, continuitions and options if they have been saved but removed.
	 * The logic may be confusing, but it is working with whatever it has in state off of the parent field template
	 * and comparing that to the db to determine what needs to be deleted.
	 * If this gets any worse it may be worth just handling deletions like we do the new options and keep track of those
	 * then pass them in here.  We could also go the other direction and remove the need to track the options and pass that in.
	 * @param fieldTemplateData map of the field template data
	 * @param finalOptions array of all options that need to be created
	 * @param tableFieldTemplateData map of table cell field template data
	 * @param formTemplateId id of the form template
	 * @param fieldsOrder order of the fields (FormInstance.fieldsOrder)
	 */
	async function updateFieldTemplates(
		fieldTemplateData: Record<string, FieldTemplate>,
		finalOptions: Option[],
		tableFieldTemplateData: Record<string, FieldTemplate>,
		formTemplateId: string,
		fieldsOrder: string[],
	) {
		try {
			const fieldTemplatesToCreate: FieldTemplate[] = [];
			const continuationDataToCreate: FieldContinuationData[] = [];
			const optionIdFieldTemplateIdMap: Record<string, string> = {};
			// This is to track the optionsOrder for all field templates.
			// and the fields order for all field templates (main and cell)
			let listOfOptionsToKeep: string[] = [];
			let listOfFieldTemplatesToKeep = fieldsOrder;
			const fieldContinuationIdsToDelete: string[] = [];
			const fieldsArray = Object.values(fieldTemplateData);
			for (let i = 0; i < fieldsArray.length; i++) {
				const field = JSON.parse(JSON.stringify(fieldsArray[i]));
				const fieldIsAKeeper = listOfFieldTemplatesToKeep.includes(field.id);

				if (field.rowOrder && field.rowOrder.length > 0) {
					listOfFieldTemplatesToKeep = listOfFieldTemplatesToKeep.concat(field.rowOrder);
				}
				if (field.optionsOrder.length > 0) {
					listOfOptionsToKeep = listOfOptionsToKeep.concat(field.optionsOrder);
					for (let i = 0; i < field.optionsOrder.length; i++) {
						const optionId = field.optionsOrder[i];
						optionIdFieldTemplateIdMap[optionId] = field.id;
					}
				}

				if (field.continuationDataId && !fieldIsAKeeper) {
					// if the field is a keeper and has a continuationDataId, then we need to keep the continuation data
					fieldContinuationIdsToDelete.push(field.continuationDataId);
				}
				if (field.continuationData && field.continuationDataId) {
					// it has a continuation data that may need to be updated.
					continuationDataToCreate.push(field.continuationData);
				}

				if (field.continuationData) {
					delete field.continuationData;
				}

				if (field.rowOrder === null) field.rowOrder = [];

				delete field.options;
				delete field.tableFields;

				if (!field.tableField) {
					delete field.tableField;
				}
				if (!field.exportType) {
					field.exportType = "text";
				}

				if (!field.formTemplate || (field.formTemplate && field.formTemplate !== formTemplateId)) {
					field.formTemplate = formTemplateId;
				}

				fieldTemplatesToCreate.push(field);
			}

			// Creates table fields and options for field templates with table fields
			for (const [newFieldTemplateId, cellFieldTemplateData] of Object.entries(tableFieldTemplateData)) {
				if (cellFieldTemplateData) {
					const tableField = cellFieldTemplateData;
					const tableFieldIsAKeeper = listOfFieldTemplatesToKeep.includes(tableField.id);

					if (tableField.continuationDataId && !tableFieldIsAKeeper) {
						// make sure we are keeping table fields continuation data if it is a keeper and has continuation data id
						fieldContinuationIdsToDelete.push(tableField.continuationDataId);
					}

					if (tableField.continuationData && tableField.continuationDataId) {
						// it has a continuation data that may need to be updated.
						continuationDataToCreate.push(tableField.continuationData);
					}

					// Table Option handling to make sure none are being deleted.
					if (tableField.optionsOrder && tableField.optionsOrder?.length > 0) {
						listOfOptionsToKeep = listOfOptionsToKeep.concat(tableField.optionsOrder);
						for (let i = 0; i < tableField.optionsOrder.length; i++) {
							const optionId = tableField.optionsOrder[i];
							optionIdFieldTemplateIdMap[optionId] = tableField.id;
						}
					}

					if (tableField.continuationData) {
						delete tableField.continuationData;
					}

					delete tableField.options;
					// }

					if (tableField.tableFields) {
						delete tableField.tableFields;
					}

					if (!tableField.exportType) {
						tableField.exportType = "text";
					}

					if (tableField.rowOrder === null) tableField.rowOrder = [];
					fieldTemplatesToCreate.push(tableField);
				}
			}

			// This method just determines what needs to be deleted.
			const optionsToDelete: Option[] = (await handleDeletionOfExistingDocuments({
				documents: finalOptions,
				docType: "Option",
				listOfKeepers: listOfOptionsToKeep,
				formTemplateId,
				online,
			})) as Option[];

			const fieldTemplatesToDelete: FieldTemplateForResolver[] = (await handleDeletionOfExistingDocuments({
				documents: fieldTemplatesToCreate,
				docType: "FieldTemplate",
				listOfKeepers: listOfFieldTemplatesToKeep,
				formTemplateId,
				online,
			})) as FieldTemplateForResolver[];

			if (online) {
				if (fieldTemplatesToCreate.length > 0) {
					handleBatchCrud(fieldTemplatesToCreate, batchCreateFieldTemplates, 25);
				}

				if (finalOptions.length > 0) {
					handleBatchCrud(finalOptions, batchCreateOptions, 25);
				}

				if (continuationDataToCreate.length > 0) {
					handleBatchCrud(continuationDataToCreate, batchCreateFieldContinuationData, 25);
				}

				if (fieldTemplatesToDelete.length > 0) {
					const parsedFieldTemplatesToDelete = parseDocsForBatchDelete(fieldTemplatesToDelete);
					handleBatchCrud(parsedFieldTemplatesToDelete, batchRemoveFieldTemplates, 25);
				}

				if (fieldContinuationIdsToDelete.length > 0) {
					// this needs to go off a list of ids instead of documents.
					const parsedContinuationsToDelete = parseIdsForBatchDelete(fieldContinuationIdsToDelete);
					handleBatchCrud(parsedContinuationsToDelete, batchRemoveFieldContinuationData, 25);
				}

				if (optionsToDelete.length > 0) {
					const parsedOptionsToDelete = parseDocsForBatchDelete(optionsToDelete);
					handleBatchCrud(parsedOptionsToDelete, batchRemoveOptions, 25);
				}
			} else {
				bulkSaveFieldTemplates(fieldTemplatesToCreate);
				bulkSaveFieldContinuationDatas(continuationDataToCreate);
				bulkSaveOptions(finalOptions);

				if (fieldTemplatesToDelete.length > 0) {
					bulkDeleteFieldTemplates(fieldTemplatesToDelete);
				}

				if (optionsToDelete.length > 0) {
					bulkDeleteOptions(optionsToDelete);
				}

				if (fieldContinuationIdsToDelete.length > 0) {
					bulkDeleteFieldContinuationDatas(fieldContinuationIdsToDelete);
				}
			}
		} catch (error) {
			console.log(`error updating field templates: `, error);
			recordSentry({ error });
		}
	}

	async function deleteFormTemplate(id: string) {
		// The Form template is put in the formTemplates array, though in this method, it should always just be one!
		// thats specifically for the s3 part
		const formTemplateJSON = await downloadFormTemplateJSON(id, online);
		if (formTemplateJSON) {
			const formTemplate = formTemplateJSON.formTemplates[0];
			const url = handleBuildFormTemplateBackupPath(formTemplate);
			// Incremenet progress
			// Setup format identical to our JSON
			const stringified: string = JSON.stringify(formTemplateJSON);
			// save to s3
			await handleUploadToS3(url, stringified);
			// verify the template saved on the s3
			const uploadSuccess: boolean = await verifyUpload(url);
			if (uploadSuccess) {
				if (online) {
					const deletePromises = [
						handleBatchCrud(parseDocsForBatchDelete(formTemplateJSON.continuationDatas), batchRemoveFieldContinuationData, 25),
						handleBatchCrud(parseDocsForBatchDelete(formTemplateJSON.fieldTemplates), batchRemoveFieldTemplates, 25),
						handleBatchCrud(parseDocsForBatchDelete(formTemplateJSON.options), batchRemoveOptions, 25),
						handleDeleteFormTemplate(id),
					];
					await Promise.all(deletePromises);
					// Now that its gone, we need to check the form template group and remove it from the templates list in state there
					const newTemplateList = { ...templates };
					if (newTemplateList[formTemplate.formTemplateGroup]) {
						// filter that shit out
						const purgedList = newTemplateList[formTemplate.formTemplateGroup].filter((template) => {
							return template.id !== id;
						});
						newTemplateList[formTemplate.formTemplateGroup] = purgedList;
						setTemplates(newTemplateList);
					}
				}
			} else {
				// Error handling.
				recordSentry({ error: `Form Template Delete failed: ${id}` });
			}
		}
	}

	// This is a sorta specific method to take one of our full document types and set it up as an array of objects id: string
	function parseDocsForBatchDelete(docs: any[]): BatchDeleteInput[] {
		let final = [];
		for (let i = 0; i < docs.length; i++) {
			const element = docs[i];
			const data = { id: element.id };
			final.push(data);
		}
		return final;
	}

	// New function to parse a list of ids for deletion.
	function parseIdsForBatchDelete(ids: string[]): BatchDeleteInput[] {
		let final = [];
		for (let i = 0; i < ids.length; i++) {
			const id = ids[i];
			const data = { id };
			final.push(data);
		}
		return final;
	}

	async function uploadFormTemplates(file: File): Promise<void> {
		const fileReader = new FileReader();
		if (file) {
			// setupOverlay({ action: "LOADING_TIMED" });
			await new Promise<void>((resolve, reject) => {
				if (file) {
					fileReader.readAsText(file, "UTF-8");
					fileReader.onload = async function (e) {
						if (e.target) {
							const jsonString = e.target.result;
							if (typeof jsonString === "string") {
								let json;
								try {
									json = JSON.parse(jsonString);
								} catch (error) {
									reject("INVALID JSON UPLOADED");
									recordSentry({ error, msg: "Invalid Json Uploaded:", showModal: true });
									return;
								}
								if (!json.v || json.type !== "FormTemplateJSON") {
									reject("OLD JSON");
									return;
								}

								try {
									if (json.formTemplates[0]) {
										const formTemplateToUpload = json.formTemplates[0];
										// Matches project id, so we're uploading data from a project into
										const donorData = prepTemplateJsonForMerge(json);
										const syncConfig: SyncConfig = {
											online,
											source: "json",
											destination: online ? "remote" : "local",
										};
										const receiverData = await prepDbForTemplateMerge(formTemplateToUpload.id, syncConfig);
										await mergeAndSaveTemplates(donorData, receiverData, syncConfig);
										// K now need to update the templates list in state
										const newTemplateList = { ...templates };
										if (newTemplateList[formTemplateToUpload.formTemplateGroup]) {
											// just push it in there
											newTemplateList[formTemplateToUpload.formTemplateGroup].push(formTemplateToUpload);
										} else {
											newTemplateList[formTemplateToUpload.formTemplateGroup] = [formTemplateToUpload];
										}

										setTemplates(newTemplateList);
									}

									resolve();
								} catch (err) {
									reject(`ERROR UPLOADING templates: ${err}`);
									recordSentry({ error: err, msg: "Error Uploading FormGroups:", showModal: true });
								}
							}
						}
					};
				}
			});
		}
	}

	const results: TemplateManagerContextType = {
		createFormTemplate,
		deleteFormTemplate,
		setTemplateToEdit,
		templates,
		templateToEdit,
		updateFieldTemplates,
		updateFormTemplate,
		uploadFormTemplates,
	};

	return <TemplateManagerContext.Provider value={results}>{children}</TemplateManagerContext.Provider>;
};

export const useTemplateManager = () => {
	const context = useContext(TemplateManagerContext);
	if (!context) {
		recordSentry({
			error: "useTemplateManager must be used within a TemplateManagerProvider",
			msg: "useTemplateManager must be used within a TemplateManagerProvider",
		});
		throw new Error("useTemplateManager must be used within a TemplateManagerProvider");
	}
	return context;
};
